import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import { ToolSpec, ToolSpecListResponse, ToolSpecShowResponse } from "./types";

export * from "./types";

const ENDPOINTS = {
  LIST: "/api/v2/org/:organization_id/user/:user_id/tool-specs",
  SHOW: "/api/v2/org/:organization_id/user/:user_id/tool-specs/:tool_spec_name",
  ADMIN_LIST: "/api/v2/admin/organizations/:organization_id/tool-specs",
  ADMIN_SHOW: "/api/v1/admin/tool-specs/:tool_spec_id",
};

export class ToolSpecAPI {
  constructor(private client: CohesiveAPIClient) {}

  /**
   * List tool specs for an organization and user
   * @param searchTerm - Optional search term to filter tool specs (via "q" param)
   * @param config - Optional axios configuration
   * @returns Promise<ToolSpec[]> - Array of tool specs
   */
  async list(
    searchTerm?: string,
    config?: AxiosRequestConfig,
  ): Promise<ToolSpec[]> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const params: Record<string, string> = { ...config?.params };
    if (searchTerm) {
      params.q = searchTerm;
    }

    const response = await this.client.axios.get<ToolSpecListResponse>(
      route(ENDPOINTS.LIST, {
        organization_id: orgId || "",
        user_id: userId || "",
      }),
      {
        ...config,
        params,
      },
    );

    return response.data.data.tool_specs;
  }

  /**
   * @deprecated Use list() instead - endpoint has been renamed
   */
  async search(
    searchTerm: string = "",
    config?: AxiosRequestConfig,
  ): Promise<ToolSpec[]> {
    return this.list(searchTerm, config);
  }

  /**
   * Get a single tool spec by name
   * @param toolSpecName - The name/key of the tool spec
   * @param config - Optional axios configuration
   * @returns Promise<ToolSpec> - The tool spec
   */
  async show(
    toolSpecName: string,
    config?: AxiosRequestConfig,
  ): Promise<ToolSpec> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<ToolSpecShowResponse>(
      route(ENDPOINTS.SHOW, {
        organization_id: orgId || "",
        user_id: userId || "",
        tool_spec_name: toolSpecName,
      }),
      config,
    );

    return response.data.data.tool_spec;
  }

  // ============================================
  // Admin Methods
  // ============================================

  /**
   * List tool specs for an organization (admin)
   * @param organizationId - The organization ID
   * @param searchTerm - Optional search term to filter tool specs (via "q" param)
   * @param config - Optional axios configuration
   * @returns Promise<ToolSpec[]> - Array of tool specs
   */
  async adminList(
    organizationId: string,
    searchTerm?: string,
    config?: AxiosRequestConfig,
  ): Promise<ToolSpec[]> {
    const params: Record<string, string> = { ...config?.params };
    if (searchTerm) {
      params.q = searchTerm;
    }

    const response = await this.client.axios.get<ToolSpecListResponse>(
      route(ENDPOINTS.ADMIN_LIST, {
        organization_id: organizationId,
      }),
      {
        ...config,
        params,
      },
    );

    return response.data.data.tool_specs;
  }

  /**
   * Get a single tool spec by ID (admin)
   * @param toolSpecId - The tool spec ID
   * @param config - Optional axios configuration
   * @returns Promise<ToolSpec> - The tool spec
   */
  async adminShow(
    toolSpecId: string,
    config?: AxiosRequestConfig,
  ): Promise<ToolSpec> {
    const response = await this.client.axios.get<ToolSpecShowResponse>(
      route(ENDPOINTS.ADMIN_SHOW, {
        tool_spec_id: toolSpecId,
      }),
      config,
    );

    return response.data.data.tool_spec;
  }
}
